import { useParams, useSearchParams } from 'react-router-dom'

import { Avatar, BlankSlate, Box, Text } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  useRateConfigurationAssignees,
} from 'generated/resources/datatable.graphql'
import { RateConfigurationDetailPageDocument } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { ExpensableRateConfigurationResourceType } from 'modules/expenses/types/Expensable'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import AssigneesModal from './AssigneesModal'
import { RedirectToRateListPage } from './RedirectToRateList'

const RateConfigurationDetailPage = ({
  resourceType,
}: {
  resourceType: ExpensableRateConfigurationResourceType
}) => {
  const { rateId } = useParams()
  const { company } = useCurrentSession()
  const companyId = company.id
  const [searchParams, setSearchParams] = useSearchParams()

  const { data: rateConfigurationData, loading } = useQuery(RateConfigurationDetailPageDocument, {
    variables: { id: Number(rateId), companyId },
  })

  const rateConfiguration = rateConfigurationData?.expenses.rateConfigurationsConnection.nodes[0]
  const { debouncedSearch, search, onSearch } = useDebouncedSearch()

  const source = useRateConfigurationAssignees(
    [
      {
        identifier: 'Employee',
        kind: ProjectionKind.Custom,
        title: i18n.t('expenses_v2.settings.rate_configurations.detail.fields.employee'),
        template: '',
        colSpan: 2,
        render: ({ fullName, avatar }) => {
          return (
            <Box flexDirection="row" gap="s12" alignItems="center">
              <Avatar size="s32" url={buildUrl(avatar)} label={fullName} />
              <Text.Normal size="200">{fullName}</Text.Normal>
            </Box>
          )
        },
      },
    ],
    {
      variables: { rateIds: [Number(rateId)], search: debouncedSearch },
    }
  )

  if (!rateConfiguration && !loading) {
    return <RedirectToRateListPage resourceType={resourceType} />
  }

  return (
    <ResponsivePage.Body loading={loading} error={null} variant="full" showHeader={false}>
      <DataTable.Provider
        source={source}
        pagination={{
          kind: 'cursor',
          itemsPerPage: 25,
          infiniteScroll: false,
        }}
      >
        <Box gap="s16" paddingTop="s16">
          <DataTable.HeadBar
            disableExport
            actions={[]}
            search={{
              placeholder: i18n.t('common.search'),
              value: search,
              onChange: onSearch,
            }}
          />
          <DataTable.Table
            emptyState={
              <BlankSlate
                illustration="wireframe"
                description={i18n.t('common.no_data_available')}
              />
            }
          />
        </Box>
      </DataTable.Provider>
      {searchParams.get('modal') === 'assignees' && rateConfiguration && (
        <AssigneesModal
          title={i18n.t('expenses_v2.settings.rate_configurations.detail.assignees_modal.title')}
          description={i18n.t(
            'expenses_v2.settings.rate_configurations.detail.assignees_modal.description'
          )}
          rateConfiguration={rateConfiguration}
          onClose={() => setSearchParams({})}
          resourceType={resourceType}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default RateConfigurationDetailPage
